import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { IssueSummary } from '@jisy/shared';
import { parentCandidatesQuery } from '../api/queries.js';
import { useDebounced } from '../lib/useDebounced.js';
import styles from './ParentPicker.module.css';

/**
 * Выбор родительской задачи для подзадачи — поле «Родитель» из формы создания Jira.
 *
 * Ищет по ключу и названию среди обычных задач выбранного раздела: подзадача живёт в разделе
 * родителя, а эпик или другая подзадача родителем ей быть не могут. Список идёт из кэша Jisy,
 * как и таблица, поэтому откликается сразу, а не после запроса к Jira.
 */
export function ParentPicker({
  projectId,
  value,
  onChange,
  excludeKey,
  autoFocus = false,
}: {
  projectId: string;
  value: Pick<IssueSummary, 'key' | 'summary'> | null;
  onChange: (value: IssueSummary | null) => void;
  /** Задача, которую нельзя выбрать: та, что сама становится подзадачей. */
  excludeKey?: string | undefined;
  /** Поиск открывается по действию человека в карточке — тогда курсор сразу в поле. */
  autoFocus?: boolean | undefined;
}): React.ReactElement {
  const { t } = useTranslation();
  const listId = useId();

  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const debounced = useDebounced(search, 250);

  /*
   * Поле поиска и строка выбранного родителя меняют друг друга, и фокус, оставшись без элемента,
   * падал бы на само окно. Поэтому он переходит следом: после выбора — на «Выбрать другую»,
   * после снятия — обратно в поиск.
   */
  const inputRef = useRef<HTMLInputElement>(null);
  const changeRef = useRef<HTMLButtonElement>(null);
  const moveFocus = useRef(false);
  useEffect(() => {
    if (!moveFocus.current) return;
    moveFocus.current = false;
    (value ? changeRef.current : inputRef.current)?.focus();
  }, [value]);

  const candidates = useQuery({
    ...parentCandidatesQuery(projectId, debounced),
    // Родитель выбран — искать больше нечего, пока его не сняли.
    enabled: value === null,
  });
  const items = (candidates.data ?? []).filter((item) => item.key !== excludeKey);

  if (value) {
    return (
      <div className={styles.chosen}>
        <span className={`mono ${styles.chosenKey}`}>{value.key}</span>
        <span className={`${styles.chosenSummary} truncate`} title={value.summary}>
          {value.summary}
        </span>
        <button
          ref={changeRef}
          type="button"
          className={styles.change}
          onClick={() => {
            moveFocus.current = true;
            onChange(null);
          }}
        >
          {t('create.parentChange')}
        </button>
      </div>
    );
  }

  function choose(item: IssueSummary | undefined): void {
    if (!item) return;
    moveFocus.current = true;
    onChange(item);
    setSearch('');
    setOpen(false);
  }

  const activeItem = items[Math.min(active, items.length - 1)];

  return (
    // Пока список открыт, Esc закрывает его, а не всю форму: окно смотрит на эту метку.
    <div className={styles.picker} {...(open ? { 'data-escape-local': '' } : {})}>
      <input
        ref={inputRef}
        className={styles.input}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        {...(open && activeItem ? { 'aria-activedescendant': `${listId}-${activeItem.key}` } : {})}
        aria-label={t('create.parent')}
        placeholder={t('create.parentPlaceholder')}
        autoFocus={autoFocus}
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
            const delta = event.key === 'ArrowDown' ? 1 : -1;
            setActive((current) => Math.min(Math.max(current + delta, 0), items.length - 1));
          } else if (event.key === 'Enter' && open) {
            event.preventDefault();
            choose(activeItem);
          } else if (event.key === 'Escape' && open) {
            event.preventDefault();
            setOpen(false);
          }
        }}
      />

      {open && (
        <ul className={styles.list} id={listId} role="listbox" aria-label={t('create.parent')}>
          {items.map((item) => (
            <li
              key={item.key}
              id={`${listId}-${item.key}`}
              role="option"
              aria-selected={item.key === activeItem?.key}
              className={`${styles.option} ${item.key === activeItem?.key ? styles.optionActive : ''}`}
              // mousedown, а не click: иначе поле теряет фокус и закрывает список до щелчка.
              onMouseDown={(event) => {
                event.preventDefault();
                choose(item);
              }}
              title={item.summary}
            >
              <span className={`mono ${styles.optionKey}`}>{item.key}</span>
              <span className="truncate">{item.summary}</span>
            </li>
          ))}

          {items.length === 0 && !candidates.isFetching && (
            <li className={styles.empty} role="presentation">
              {t('create.parentEmpty')}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
